import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  CopyableText,
  CustomSearch,
  errorNotificationBuilder,
  useFeatureToggle,
  useFetch,
  useNotification,
  useTitle,
  NotificationTypes,
} from '@css-xp/base';
import OfficeBuildingIcon from '@atlaskit/icon/glyph/office-building';
import { abgGraphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { Text } from '@atlaskit/primitives';
import { PageBackground, PageBackgroundV2 } from '@css-xp/page-background';
import { ENABLE_DESIGN_ITERATION_1, PPC_PAGE_TITLE, routePaths, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { Background } from '@css-xp/page-background/src/background-image';
import { usePersistedCacheQuery } from '@css-xp/ppc-custom-hooks';
import {
  FetchAvailableInvoiceGroupsForOrderReviewQuery,
  FetchAvailableInvoiceGroupsForOrderReviewQueryVariables,
  FetchProductsForOrganizationPpcQuery,
  FetchProductsForOrganizationPpcQueryVariables,
  FilterOption,
} from '@css-xp/graphtypes/src/generated-types-new';
import { OrganisationDetailPageLoader, renderAccordionSectionLoader } from '@css-xp/ppc-page-loaders';
import { fetchBillingProfilesForTransactionAccountInPPC } from '@css-xp/ppc-billing-profile-table';
import { fetchProductsForOrganization } from './queries/fetch-org-products';
import { fetchAvailableInvoiceGroups } from './queries/fetch-available-invoice-groups';
import { truthy } from '@css-xp/ppc-cart-page/src/utils';
import { fetchOrganizationById } from '@atlassian/temp-proxies/src/organization/organization';
import { failureMessage } from '@css-xp/ppc-common';
import { OrganisationProductTable } from './organisation-product-table';
import SitesTable from './components/sites-table/sites-table';
import './organisation-detail-page.scss';

interface FormData {
  searchTerm: string;
}

export const OrganisationDetailPage = () => {
  const { organisationId, txaId } = useParams<{ organisationId: string; txaId: string }>();
  const isFirstLoad = useRef(true);
  const invoiceGroupIdsRef = useRef<any>([]);
  const [searchedInput, setSearchedInput] = useState<string>();
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isSandboxSupportForSubscriptionsEnabled = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_SANDBOX_SUPPORT_FOR_SUBSCRIPTIONS
  );
  const isMultiInstanceAppsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_INSTANCE_APPS);
  const { setNotificationSent, sendErrorNotification } = useNotification();
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [isSearchedDataLoading, setIsSearchedDataLoading] = useState<boolean>(false);

  useTitle(organisationId, PPC_PAGE_TITLE.ORGANISATION);

  /**
   * useFetch hook cannot be called after early return
   * Hence, we are calling useFetch hook before early return
   */
  const orgDetails = useFetch({
    api: () => fetchOrganizationById(organisationId),
    deps: [fetchOrganizationById, organisationId],
  });
  const { data: organizationDetails, isLoading: isOrgDetailsLoading } = orgDetails;

  const {
    data: invoiceGroupDetails,
    loading: invoiceGroupLoading,
    error: invoiceGroupDetailsError,
  } = usePersistedCacheQuery<
    FetchAvailableInvoiceGroupsForOrderReviewQuery,
    FetchAvailableInvoiceGroupsForOrderReviewQueryVariables
  >(fetchAvailableInvoiceGroups, {
    variables: {
      id: txaId,
    },
    fetchPolicy: 'cache-first',
    skip: !txaId,
  });

  // Fetch products for the organization table
  const productTableDataState = useQuery<
    FetchProductsForOrganizationPpcQuery,
    FetchProductsForOrganizationPpcQueryVariables
  >(fetchProductsForOrganization, {
    variables: {
      organizationId: organisationId,
      transactionAccountId: txaId,
    },
    displayName: 'fetchCloudSiteProducts',
    nextFetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const ansClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.ANS), []);

  const { error: fetchProductsForOrganizationError } = productTableDataState;

  useEffect(() => {
    if (fetchProductsForOrganizationError) {
      const notificationOptions = errorNotificationBuilder()
        .withId('fetch-products-for-organization-error')
        .withHeader('An error occurred while fetching products')
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  }, [fetchProductsForOrganizationError]);

  useEffect(() => {
    if (invoiceGroupDetailsError) {
      const notificationOptions = errorNotificationBuilder()
        .withId('fetch-available-invoice-groups-error')
        .withHeader('An error occurred while fetching available invoice groups')
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .isVersion(NotificationTypes.FLAG)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  }, [invoiceGroupDetailsError]);

  const isLoading =
    productTableDataState.loading || invoiceGroupLoading || isOrgDetailsLoading || isSearchedDataLoading;

  if (isDesignIteration1Enabled && !searchedInput && isFirstLoad.current && isLoading) {
    return <OrganisationDetailPageLoader />;
  }

  const getPageHeading = () => {
    if (isDesignIteration1Enabled) {
      return (
        <div className="flex gap2">
          <OfficeBuildingIcon label="" />
          <div className="flex-column">
            {organizationDetails?.organizationName}
            <Text color="color.text.subtle">
              <CopyableText text={organisationId} />
            </Text>
          </div>
        </div>
      );
    }
    return (
      <div className="page-heading">
        <div className="org-heading">{organizationDetails?.organizationName}</div>
        <div className="org-sub-heading">{organisationId && <CopyableText text={organisationId} />}</div>
      </div>
    );
  };

  const helperMessage = (() => {
    return isDesignIteration1Enabled
      ? `Enter E#, product name, subscription, or billing profile`
      : `Enter E-#, or product name`;
  })();

  const buildSearchFilters = (searchInput: string) => {
    searchInput = searchInput.trim();

    return [{ productNameText: searchInput }, { entitlementNumber: searchInput }];
  };

  const buildBillingProfileSearchFilters = (searchInput: string) => {
    searchInput = searchInput.trim();

    return [{ name: searchInput }, { invoiceGroupNumber: searchInput }];
  };

  const onSearch = async (formData: FormData) => {
    if (isDesignIteration1Enabled && isFirstLoad.current) {
      isFirstLoad.current = false;
    }
    const searchInput = formData?.searchTerm;
    setSearchedInput(searchInput);
    const filterOptions: FilterOption[] = [];

    if (isDesignIteration1Enabled) {
      setIsSearchedDataLoading(true);
      if (invoiceGroupIdsRef.current.length === 0) {
        invoiceGroupIdsRef.current =
          productTableDataState?.data?.productsForOrganizationPPC.map((data) => ({
            id: data?.invoiceGroupId,
          })) || [];
      }
      ansClient
        .query({
          query: fetchBillingProfilesForTransactionAccountInPPC,
          variables: {
            id: txaId,
            refetchDefaultPaymentMethod: false,
            filterOptions: [
              {
                and: [
                  {
                    key: 'or',
                    value: JSON.stringify(
                      invoiceGroupIdsRef.current?.map((invoiceGroup: any) => ({ id: invoiceGroup?.id }))
                    ),
                  },
                  {
                    key: 'or',
                    value: JSON.stringify(buildBillingProfileSearchFilters(searchInput)),
                  },
                ],
              },
            ],
          },
        })
        .then(({ data }) => {
          if (searchInput) {
            const invoiceGroupIds = data.transactionAccount.billingProfiles?.nodes.map((invoiceGroup: any) => ({
              invoiceGroupId: invoiceGroup?.id,
            }));
            filterOptions.push({
              or: [
                {
                  key: 'or',
                  value: JSON.stringify(buildSearchFilters(searchInput)),
                },
                ...(invoiceGroupIds.length
                  ? [
                      {
                        key: 'or',
                        value: JSON.stringify(invoiceGroupIds),
                      },
                    ]
                  : []),
              ],
            });
            setFilterOptions(filterOptions);
          }

          productTableDataState.refetch({
            organizationId: organisationId,
            transactionAccountId: txaId,
            filterOptions,
          });
          setIsSearchedDataLoading(false);
        })
        .catch(() => {
          setIsSearchedDataLoading(false);
          const notificationOptions = errorNotificationBuilder()
            .withId('fetch-billing-profiles-error')
            .withHeader('Search failed')
            .withMessage('An error occurred while fetching billing profiles, please try again.')
            .withDismissAfter(5000)
            .isVersion(NotificationTypes.FLAG)
            .build();
          sendErrorNotification(notificationOptions);
          setNotificationSent(true);
        });
    } else {
      if (searchInput) {
        filterOptions.push({
          key: 'or',
          value: JSON.stringify(buildSearchFilters(searchInput)),
        });
      }
      productTableDataState.refetch({
        organizationId: organisationId,
        transactionAccountId: txaId,
        filterOptions,
      });
    }
  };

  const renderAppPage = () => {
    return (
      <div className="organisation-detail-page-container">
        <div className="organisation-detail-page-container-search">
          <CustomSearch
            placeholder="Search"
            onSearch={onSearch}
            isLoading={productTableDataState.loading}
            isClearable={true}
            isRefetchOnClearEnabled={true}
            helperMessage={helperMessage}
          />
        </div>
        {isDesignIteration1Enabled && isLoading ? (
          <>
            {renderAccordionSectionLoader()}
            <br />
            {renderAccordionSectionLoader(true)}
          </>
        ) : (
          <>
            {!!productTableDataState?.data?.productsForOrganizationPPC?.length && (
              <div className="organisation-detail-page-container-products">
                <OrganisationProductTable
                  txaId={txaId}
                  invoiceGroupDetails={(
                    invoiceGroupDetails?.transactionAccount?.availableInvoiceGroups?.nodes ?? []
                  ).filter(truthy)}
                  invoiceGroupLoading={invoiceGroupLoading}
                  dataState={productTableDataState}
                  version={isDesignIteration1Enabled && 3}
                  organizationName={organizationDetails?.organizationName || ''}
                />
              </div>
            )}
            <div className="organisation-detail-page-container-sites">
              <SitesTable
                txaId={txaId}
                organisationId={organisationId}
                invoiceGroupDetails={(
                  invoiceGroupDetails?.transactionAccount?.availableInvoiceGroups?.nodes ?? []
                ).filter(truthy)}
                invoiceGroupLoading={invoiceGroupLoading}
                searchedInput={searchedInput}
                {...(isDesignIteration1Enabled && { subscriptionColumnVersion: 2 })}
                isSandboxSupportForSubscriptionsEnabled={isSandboxSupportForSubscriptionsEnabled}
                version={isDesignIteration1Enabled && 3}
                filterOptions={isDesignIteration1Enabled && searchedInput ? filterOptions : undefined}
                areProductsEmpty={!productTableDataState.data?.productsForOrganizationPPC?.length}
                isMultiInstanceAppsEnabled={isMultiInstanceAppsEnabled}
              />
            </div>
          </>
        )}
      </div>
    );
  };

  const breadcrumbsItems = [
    {
      title: 'Home',
      href: routePaths.home,
    },
    {
      title: `${organizationDetails?.organizationName}`,
      href: routePaths.organisationDetails,
    },
  ];

  if (isDesignIteration1Enabled) {
    return (
      <div className="organisation-detail-page-v3">
        <PageBackgroundV2 heading={getPageHeading()} breadcrumbsProps={{ items: breadcrumbsItems }}>
          {renderAppPage()}
        </PageBackgroundV2>
      </div>
    );
  }

  return (
    <div className="organisation-detail-page">
      <PageBackground
        heading={getPageHeading()}
        imageKey={Background.PPC_HEADER_BACKGROUND}
        className="page-background gray-bg"
      />
      {renderAppPage()}
    </div>
  );
};
